import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { GroupsRounded } from "@mui/icons-material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Box, CircularProgress, ListItemIcon, ListItemText, Typography } from "@mui/material";

import TableEmptyState from "@components/Table/TableEmptyState";
import {
  TableToolbar,
  TableToolbarDropdown,
  TableToolbarLeft,
  TableToolbarRight,
  TableToolbarTitle,
} from "@components/TableToolbar/TableToolbar";
import { TableToolbarDropdownMenuItem } from "@components/TableToolbar/TableToolbar";

import C from "@constants/constants";
import routes from "@constants/routes";

import { ContactType } from "@interfaces/contact.type";

import CampaignsTable from "@pages/Campaigns/components/CampaignsTable";
import { exportCampaignContacts, exportCampaigns } from "@pages/Campaigns/utils";

import { useCampaignsQuery, useDeleteDraftCampaignQuery } from "@queries/campaign.query";

import { PopupActions } from "@store/popupStore";

import contentCardStyles from "@theme/contentCardStyles";
import theme from "@theme/index";

export default function TechnologyCampaigns() {
  const { technologyId } = useParams();
  const navigate = useNavigate();
  const [selection, setSelection] = useState<string[]>([]);

  const { data: campaigns, status } = useCampaignsQuery(technologyId);
  const { mutateAsync: deleteCampaign } = useDeleteDraftCampaignQuery();

  const isLoading = status === "pending";

  const handleDeleteCampaignClick = (campaignId: string) => {
    PopupActions.openConfirmation({
      content: (
        <Typography sx={{ color: theme.palette.text.primary, mt: 1 }}>
          Are you sure you would like to delete this Campaign Draft? You{" "}
          <strong>
            <u>cannot</u>
          </strong>{" "}
          undo this action.
        </Typography>
      ),
      onConfirm: () => {
        if (!technologyId || !campaignId) return;
        void deleteCampaign({ campaignId, technologyId });
      },
      title: "Delete Campaign Draft",
    });
  };

  const selectedCampaignIds = useMemo(() => {
    if (!campaigns) return [];

    const selectedCampaigns = campaigns?.filter((campaign) => selection.includes(campaign.id));

    const filteredCampaigns = selectedCampaigns?.filter((campaign) => {
      if (campaign.scheduledAt) {
        return false;
      }
      return campaign.sentCount;
    });

    return filteredCampaigns.map((campaign) => campaign.id);
  }, [campaigns, selection]);

  if (isLoading) {
    return (
      <Box
        sx={{
          ...contentCardStyles,
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return campaigns?.length === 0 ? (
    <TableEmptyState
      handleButtonClick={() => navigate(`${routes.TECHNOLOGY}/${technologyId}/contacts`)}
      heading="Accelerate Your Outreach"
      resourceLink={C.LOOM_CAMPAIGNS}
      state="campaigns"
      supportingText="Select contacts from your Technology to create a new campaign."
    />
  ) : (
    <Box sx={{ ...contentCardStyles }}>
      {campaigns && (
        <TableToolbar>
          <TableToolbarLeft>
            <TableToolbarTitle
              resourceLink=""
              tableSubtitle="Manage your email Campaigns and track their performance."
              tableTitle="Campaigns"
              workflow="portfolio"
            />
          </TableToolbarLeft>
          <TableToolbarRight>
            <TableToolbarDropdown
              actionsLabel="Actions"
              selectedIds={selectedCampaignIds}
              tooltipText="Export selected campaigns to CSV."
            >
              <TableToolbarDropdownMenuItem
                onClick={async () => {
                  if (!campaigns) return;

                  const filteredCampaigns = campaigns.filter((campaign) => selectedCampaignIds.includes(campaign.id));

                  const contacts = filteredCampaigns
                    .flatMap((campaign) => campaign.emails)
                    .map((contact) => ({
                      ...contact,
                      id: contact.contactId,
                    }));

                  const selectedContactIds = contacts.map((contact) => contact.contactId);

                  await exportCampaignContacts(
                    selectedContactIds,
                    contacts as unknown as ContactType[],
                    filteredCampaigns,
                  );
                }}
              >
                <ListItemIcon>
                  <GroupsRounded sx={{ color: theme.palette.grey[700] }} />
                </ListItemIcon>
                <ListItemText>Export Contacts</ListItemText>
              </TableToolbarDropdownMenuItem>
              <TableToolbarDropdownMenuItem onClick={() => exportCampaigns(selection, campaigns)}>
                <ListItemIcon>
                  <DownloadRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export Campaign Data</ListItemText>
              </TableToolbarDropdownMenuItem>
            </TableToolbarDropdown>
          </TableToolbarRight>
        </TableToolbar>
      )}
      {/* <TableToolbar description="Manage your email Campaigns and track their performance." title="Campaigns" /> */}
      <CampaignsTable
        campaigns={campaigns ?? []}
        handleDeleteCampaignClick={handleDeleteCampaignClick}
        selection={selection}
        setSelection={setSelection}
      />
    </Box>
  );
}
